import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { YLogSearchInput } from '../../../ui/YLogSearchInput';
import { YSelect } from '../../../ui/YSelect';
import { YInput } from '../../../ui/YInput';
import { YButton } from '../../../ui/YButton';
import {
  HUNT_SCOPE_ALL,
  HUNT_SCOPE_CHIPS,
  huntLogFiltersActive,
} from '../../../utils/huntLogFilters';

/**
 * Search + year / date-range / scope filters for hunt log lists (personal Hunts page).
 */
export function HuntLogFilters({
  searchTerm,
  onSearchChange,
  year,
  onYearChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  scope = HUNT_SCOPE_ALL,
  onScopeChange,
  availableYears = [],
  resultCount = 0,
  totalCount = 0,
  viewMode = 'grid',
  onViewModeChange,
}) {
  const hasActiveFilters = huntLogFiltersActive({
    query: searchTerm,
    year,
    startDate,
    endDate,
    scope,
  });

  const handleYearChange = useCallback(
    (e) => {
      const next = e.target.value;
      onYearChange(next);
      if (next) {
        onStartDateChange('');
        onEndDateChange('');
      }
    },
    [onYearChange, onStartDateChange, onEndDateChange],
  );

  const handleStartChange = useCallback(
    (e) => {
      onStartDateChange(e.target.value);
      if (e.target.value) onYearChange('');
    },
    [onStartDateChange, onYearChange],
  );

  const handleEndChange = useCallback(
    (e) => {
      onEndDateChange(e.target.value);
      if (e.target.value) onYearChange('');
    },
    [onEndDateChange, onYearChange],
  );

  const handleClear = useCallback(() => {
    onSearchChange('');
    onYearChange('');
    onStartDateChange('');
    onEndDateChange('');
    if (onScopeChange) onScopeChange(HUNT_SCOPE_ALL);
  }, [onSearchChange, onYearChange, onStartDateChange, onEndDateChange, onScopeChange]);

  return (
    <div className="hunts-shell__filters" data-testid="hunt-log-filters">
      <div className="hunts-shell__filters-row">
        <YLogSearchInput
          id="hunt-log-search"
          className="hunts-shell__filters-search"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search location, notes, deer…"
          ariaLabel="Search hunt log"
        />
        <div className="hunts-shell__filter-field">
          <label className="hunts-shell__filter-label" htmlFor="hunt-log-year">
            Year
          </label>
          <YSelect
            id="hunt-log-year"
            value={year}
            onChange={handleYearChange}
            aria-label="Filter by year"
          >
            <option value="">All years</option>
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </YSelect>
        </div>
        <div className="hunts-shell__filter-field">
          <label className="hunts-shell__filter-label" htmlFor="hunt-log-start">
            From
          </label>
          <YInput
            id="hunt-log-start"
            type="date"
            value={startDate}
            onChange={handleStartChange}
            aria-label="Filter from date"
          />
        </div>
        <div className="hunts-shell__filter-field">
          <label className="hunts-shell__filter-label" htmlFor="hunt-log-end">
            To
          </label>
          <YInput
            id="hunt-log-end"
            type="date"
            value={endDate}
            onChange={handleEndChange}
            min={startDate || undefined}
            aria-label="Filter to date"
          />
        </div>
      </div>

      {typeof onScopeChange === 'function' ? (
        <div
          className="hunts-shell__filter-chips"
          role="group"
          aria-label="Hunt scope filters"
          data-testid="hunt-log-scope-chips"
        >
          {HUNT_SCOPE_CHIPS.map((chip) => {
            const active = scope === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                className={`hunts-shell__filter-chip${active ? ' is-active' : ''}`}
                aria-pressed={active}
                onClick={() => onScopeChange(chip.id)}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="hunts-shell__filters-footer">
        {totalCount > 0 ? (
          <p className="hunts-shell__filters-summary" data-testid="hunt-log-filter-summary">
            Showing {resultCount} of {totalCount} sit{totalCount === 1 ? '' : 's'}
          </p>
        ) : (
          <span className="hunts-shell__filters-summary hunts-shell__filters-summary--empty" />
        )}
        <div className="hunts-shell__filters-footer-actions">
          {hasActiveFilters ? (
            <YButton
              type="button"
              variant="ghost"
              size="sm"
              className="hunts-shell__filters-clear"
              onClick={handleClear}
              data-testid="hunt-log-clear-filters"
            >
              Clear filters
            </YButton>
          ) : null}
          {typeof onViewModeChange === 'function' ? (
            <div className="y-view-toggle hunts-shell__view-toggle" role="group" aria-label="Hunt list layout">
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => onViewModeChange('list')}
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
              >
                List
              </button>
              <button
                type="button"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => onViewModeChange('grid')}
                aria-label="Card view"
                aria-pressed={viewMode === 'grid'}
              >
                Grid
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

HuntLogFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  year: PropTypes.string.isRequired,
  onYearChange: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  onStartDateChange: PropTypes.func.isRequired,
  endDate: PropTypes.string.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  scope: PropTypes.string,
  onScopeChange: PropTypes.func,
  availableYears: PropTypes.arrayOf(PropTypes.string),
  resultCount: PropTypes.number,
  totalCount: PropTypes.number,
  viewMode: PropTypes.oneOf(['grid', 'list']),
  onViewModeChange: PropTypes.func,
};
